import { CONTENT_TYPES } from 'dashboard/components-next/message/constants';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { useCallsStore } from 'dashboard/stores/calls';
import types from 'dashboard/store/mutation-types';
import sipVoiceClient from 'dashboard/api/channel/voice/sipVoiceClient';
import { VOICE_CALL_PROVIDERS } from 'dashboard/helper/inbox';

// For SIP, the ringing popup must surface only on a browser that actually
// registered the extension (an inbox member). The call message is broadcast to
// every agent with conversation access, so without this gate an inbound SIP
// call would pop on every admin even though only registered browsers received
// the INVITE and can answer it.
// "Owns" means this browser initialized the SIP device for the inbox — which
// Dashboard.vue only does for inbox members. We deliberately don't gate on the
// live isRegistered() state: it flaps during re-registration, and gating on it
// would suppress the popup (and the store entry) on the member's own browser,
// which then breaks answering (joinCall can't see it's a SIP call and falls
// back to the Twilio conference path).
const sipBrowserOwnsInbox = inboxId =>
  sipVoiceClient.initialized &&
  Number(sipVoiceClient.inboxId) === Number(inboxId);

const isUnownedInboundSipCall = ({ provider, callDirection, inboxId }) =>
  provider === VOICE_CALL_PROVIDERS.SIP &&
  callDirection === 'inbound' &&
  !sipBrowserOwnsInbox(inboxId);

export const TERMINAL_STATUSES = [
  'completed',
  'busy',
  'failed',
  'no-answer',
  'canceled',
  'missed',
  'ended',
];

export const isInbound = direction => direction === 'inbound';

const isVoiceCallMessage = message => {
  return CONTENT_TYPES.VOICE_CALL === message?.content_type;
};

const shouldSkipCall = (callDirection, senderId, currentUserId) => {
  return callDirection === 'outbound' && senderId !== currentUserId;
};

const extractAssigneeId = conversation => {
  return conversation?.assignee_id || conversation?.meta?.assignee?.id || null;
};

const isAssignedToAnotherAgent = (assigneeId, currentUserId) => {
  if (currentUserId == null) return false;
  return !!assigneeId && assigneeId !== currentUserId;
};

const shouldShowCall = ({
  callDirection,
  senderId,
  assigneeId,
  currentUserId,
}) => {
  if (shouldSkipCall(callDirection, senderId, currentUserId)) return false;
  // Outbound calls are scoped to the initiator via shouldSkipCall; the
  // conversation may be auto-assigned to a different agent on creation, so
  // skip the assignee filter for outbound to avoid hiding the caller's own widget.
  if (callDirection === 'outbound') return true;
  return !isAssignedToAnotherAgent(assigneeId, currentUserId);
};

// Offline/busy agents shouldn't get a ringing popup for inbound calls, but
// outbound calls always belong to the initiator regardless of their status,
// and existing (already-surfaced) calls keep going so a status change
// mid-call doesn't yank away an active widget.
const shouldRingInbound = (callDirection, currentUserAvailability) => {
  if (callDirection === 'outbound') return true;
  return currentUserAvailability === 'online';
};

function extractCallerSnapshot(message) {
  // Snapshot caller info from the message at add-time so the widget can keep
  // rendering it after the user navigates away from a conversation list that
  // had the conversation hydrated (and Vuex evicts it from the store).
  // Only incoming messages carry the contact as the sender; on outbound calls
  // the sender is the initiating agent, so skip the snapshot and let the widget
  // fall back to the conversation's contact (conversation.meta.sender).
  if (message?.message_type !== MESSAGE_TYPE.INCOMING) return null;
  const sender = message?.sender;
  if (!sender) return null;
  return {
    name: sender.name,
    phone: sender.phone_number,
    avatar: sender.avatar || sender.thumbnail,
    additionalAttributes: sender.additional_attributes || {},
  };
}

function extractCallData(message) {
  const call = message?.call || {};
  return {
    callSid: call.provider_call_id,
    callId: call.id,
    provider: call.provider,
    status: call.status,
    callDirection: call.direction === 'outgoing' ? 'outbound' : 'inbound',
    conversationId: message?.conversation_id,
    inboxId: message?.inbox_id ?? message?.conversation?.inbox_id,
    assigneeId: extractAssigneeId(message?.conversation),
    senderId: message?.sender?.id,
    caller: extractCallerSnapshot(message),
  };
}

export function handleVoiceCallCreated(
  message,
  currentUserId,
  currentUserAvailability
) {
  if (!isVoiceCallMessage(message)) return;

  const {
    callSid,
    callId,
    provider,
    callDirection,
    conversationId,
    inboxId,
    assigneeId,
    senderId,
  } = extractCallData(message);

  if (isUnownedInboundSipCall({ provider, callDirection, inboxId })) return;

  if (
    !shouldShowCall({
      callDirection,
      senderId,
      assigneeId,
      currentUserId,
    })
  ) {
    return;
  }

  if (!shouldRingInbound(callDirection, currentUserAvailability)) return;

  const callsStore = useCallsStore();
  callsStore.addCall({
    callSid,
    callId,
    provider,
    conversationId,
    inboxId,
    callDirection,
    senderId,
    caller: extractCallerSnapshot(message),
  });
}

export function handleVoiceCallUpdated(
  commit,
  message,
  currentUserId,
  currentUserAvailability
) {
  if (!isVoiceCallMessage(message)) return;

  const {
    callSid,
    callId,
    provider,
    status,
    callDirection,
    conversationId,
    inboxId,
    assigneeId,
    senderId,
  } = extractCallData(message);

  const callsStore = useCallsStore();

  callsStore.handleCallStatusChanged({ callSid, status, conversationId });

  commit(types.UPDATE_MESSAGE_CALL_STATUS, {
    conversationId,
    callStatus: status,
    callSid,
  });

  if (
    !shouldShowCall({
      callDirection,
      senderId,
      assigneeId,
      currentUserId,
    })
  ) {
    callsStore.removeCall(callSid);
    return;
  }

  if (status === 'ringing') {
    if (isUnownedInboundSipCall({ provider, callDirection, inboxId })) return;
    if (!shouldRingInbound(callDirection, currentUserAvailability)) return;

    callsStore.addCall({
      callSid,
      callId,
      provider,
      conversationId,
      inboxId,
      callDirection,
      senderId,
      caller: extractCallerSnapshot(message),
    });
  }
}

// Initialize the SIP device for a given inbox.
// Should be called when an agent opens a SIP inbox channel.
// The SIP device registers to FreeSWITCH via WSS and listens for incoming INVITEs.
// Incoming calls are reported to the backend (dedup-safe) and the ActionCable
// broadcast drives the call notification UI — same flow as Twilio.
export async function initializeSipDevice(inboxId) {
  if (!inboxId) return;
  try {
    await sipVoiceClient.initializeDevice(inboxId);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[SIP] Failed to initialize device:', e);
  }
}

export function destroySipDevice() {
  sipVoiceClient.destroyDevice();
}

export function answerSipCall() {
  return sipVoiceClient.answerCall();
}

export function makeOutboundSipCall(target, { conversationId = null } = {}) {
  return sipVoiceClient.makeCall(target, { conversationId });
}

export function endSipCall() {
  sipVoiceClient.endCall();
}

export function holdSipCall() {
  sipVoiceClient.holdCall();
}

export function unholdSipCall() {
  sipVoiceClient.unholdCall();
}

export function isSipCallHeld() {
  return sipVoiceClient.isCallHeld;
}

export function transferSipCall(target) {
  sipVoiceClient.transferCall(target);
}

export function startSipConsultativeCall(target) {
  return sipVoiceClient.startConsultativeCall(target);
}

export function completeSipAttendedTransfer() {
  sipVoiceClient.completeAttendedTransfer();
}

export function cancelSipConsultativeTransfer() {
  sipVoiceClient.cancelConsultativeTransfer();
}

export function isSipConsultativeActive() {
  return sipVoiceClient.isConsultativeCallActive;
}

/**
 * Format a phone number for SIP outbound calls (FusionPBX local Brazilian format).
 * - Strips +55 or leading 55 (when followed by 10-11 digits) and adds a leading 0
 * - Leaves short numeric identifiers (extensions) unchanged
 * - Leaves already-formatted numbers (start with 0) unchanged
 */
export function formatSipPhoneTarget(number) {
  if (!number) return number;
  const str = String(number).trim();
  // Short numeric-only strings are extensions — leave them as-is
  if (/^\d{1,6}$/.test(str)) return str;
  // Already formatted (starts with 0)
  if (str.startsWith('0')) return str;
  // Strip +55 or leading 55, split into DDD (2 digits) + local (8 or 9 digits)
  const match = str.match(/^\+?55(\d{2})(\d{8,9})$/);
  if (match) {
    const ddd = match[1];
    const local = match[2];
    // Brazilian mobile numbers added a 9th digit (nono dígito). If the stored
    // number still has 8 local digits (old format), prepend the 9 so the
    // carrier receives the current 9-digit local number.
    const normalized = local.length === 8 ? `9${local}` : local;
    return `0${ddd}${normalized}`;
  }
  return str;
}

export function syncConversationCallVisibility(conversation, currentUserId) {
  const assigneeId = extractAssigneeId(conversation);
  if (!isAssignedToAnotherAgent(assigneeId, currentUserId)) return;

  // Outbound calls belong to the initiator regardless of who the conversation
  // is currently assigned to (auto-assignment may flip mid-call). Mirror
  // shouldShowCall's outbound exception so an in-progress outbound call isn't
  // ripped out from under the caller when the conversation reassigns.
  const callsStore = useCallsStore();
  const callsToRemove = callsStore.calls.filter(
    call =>
      call.conversationId === conversation.id &&
      !shouldShowCall({
        callDirection: call.callDirection,
        senderId: call.senderId,
        assigneeId,
        currentUserId,
      })
  );
  callsToRemove.forEach(call => callsStore.removeCall(call.callSid));
}
